import { storageKey } from '../edition';

export const STORAGE_KEY = storageKey('caseExplorerResults');
export const STORAGE_VERSION = '1';
export const BOOKMARKS_KEY = storageKey('caseExplorerBookmarks');
export const BOOKMARK_DATA_KEY = storageKey('caseExplorerBookmarkData');
export const FILTERS_KEY = storageKey('caseExplorerFilters');
export const PAGE_KEY = storageKey('caseExplorerPage');

export function loadBookmarks() {
  try {
    const raw = localStorage.getItem(BOOKMARKS_KEY);
    return raw ? JSON.parse(raw).map(String) : [];
  } catch {
    return [];
  }
}

export function loadBookmarkData() {
  try {
    const raw = localStorage.getItem(BOOKMARK_DATA_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function updateBookmarks(ids, data) {
  try {
    localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(ids));
    localStorage.setItem(BOOKMARK_DATA_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function loadFilters() {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveFilters(filters) {
  try {
    localStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
  } catch {
    /* ignore */
  }
}

export function loadPage() {
  try {
    const raw = localStorage.getItem(PAGE_KEY);
    return raw ? Number(raw) : 1;
  } catch {
    return 1;
  }
}

export function savePage(page) {
  try {
    localStorage.setItem(PAGE_KEY, String(page));
  } catch {
    /* ignore */
  }
}

export function loadResults() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version === STORAGE_VERSION) {
      return {
        query: typeof parsed.query === 'string' ? parsed.query : '',
        aiCases: Array.isArray(parsed.aiCases) ? parsed.aiCases : []
      };
    }
    localStorage.removeItem(STORAGE_KEY);
    return null;
  } catch {
    return null;
  }
}

export function saveResults(query, aiCases) {
  try {
    const payload = { version: STORAGE_VERSION, query, aiCases };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function loadCaseNote(id) {
  try {
    return localStorage.getItem(storageKey(`case-note:${id}`)) || '';
  } catch {
    return '';
  }
}

export function saveCaseNote(id, note) {
  try {
    localStorage.setItem(storageKey(`case-note:${id}`), note);
  } catch {
    /* ignore */
  }
}

// -----------------------------------------------------------------------------
// Client-side caches for faster searches and case detail loading
// -----------------------------------------------------------------------------

export const SEARCH_CACHE_KEY = storageKey('caseExplorerSearchCache');
export const DETAIL_CACHE_KEY = storageKey('caseExplorerDetailCache');
export const SEARCH_CACHE_TTL = 1000 * 60 * 60; // 1 hour
export const DETAIL_CACHE_TTL = 1000 * 60 * 60 * 24; // 24 hours

function loadJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function saveJSON(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

// ---- Search cache -----------------------------------------------------------

export function loadSearchCache() {
  const raw = loadJSON(SEARCH_CACHE_KEY, {});
  const now = Date.now();
  const out = {};
  for (const [key, entry] of Object.entries(raw)) {
    if (entry && now - (entry.ts || 0) < SEARCH_CACHE_TTL) {
      out[key] = entry.cases || [];
    }
  }
  if (Object.keys(out).length !== Object.keys(raw).length) {
    saveSearchCache(out);
  }
  return out;
}

export function saveSearchCache(cache) {
  const now = Date.now();
  const wrapped = {};
  for (const [key, cases] of Object.entries(cache)) {
    wrapped[key] = { cases, ts: now };
  }
  saveJSON(SEARCH_CACHE_KEY, wrapped);
}

// ---- Case detail cache ------------------------------------------------------

export function loadCaseDetail(id) {
  const cache = loadJSON(DETAIL_CACHE_KEY, {});
  const entry = cache[id];
  const now = Date.now();
  if (!entry || now - (entry.ts || 0) > DETAIL_CACHE_TTL) {
    delete cache[id];
    saveJSON(DETAIL_CACHE_KEY, cache);
    return null;
  }
  return entry.detail;
}

export function saveCaseDetail(id, detail) {
  const cache = loadJSON(DETAIL_CACHE_KEY, {});
  const now = Date.now();
  cache[id] = { detail, ts: now };
  for (const key of Object.keys(cache)) {
    if (now - (cache[key].ts || 0) > DETAIL_CACHE_TTL) {
      delete cache[key];
    }
  }
  const keys = Object.keys(cache);
  if (keys.length > 20) {
    const oldest = keys.sort((a, b) => cache[a].ts - cache[b].ts)[0];
    delete cache[oldest];
  }
  saveJSON(DETAIL_CACHE_KEY, cache);
}
